import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Pagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Edit,
  ExpandMore,
  Info,
  FilePresent
} from '@mui/icons-material';
import {
  getPendingRequirements,
  approveRequirement,
  rejectRequirement,
  deleteAllPendingRequirements
} from '../api/masterRequirementsApi';

const PendingRequirementsPanel = ({ userPermissions, showSuccess, showError, onRefreshCount }) => {
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    page_size: 20
  });
  
  // Estados para edición
  const [editingReq, setEditingReq] = useState(null);
  const [editedName, setEditedName] = useState('');
  const [editedText, setEditedText] = useState('');
  
  // Estados para rechazo
  const [rejectingReq, setRejectingReq] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // Estado para borrar todos los pendientes
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  
  // Verificar permisos
  const canValidate = userPermissions?.can_edit || userPermissions?.can_delete;
  
  useEffect(() => {
    if (canValidate) {
      loadRequirements();
    }
  }, [pagination.current_page, canValidate]);
  
  const loadRequirements = async () => {
    try {
      setLoading(true);
      const data = await getPendingRequirements(pagination.current_page, pagination.page_size);
      setRequirements(data.requirements);
      setPagination(data.pagination);
    } catch (error) {
      showError('Error loading pending requirements');
      console.error('Error loading pending requirements:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleApprove = async (requirementId, withEdits = false) => {
    try {
      const data = withEdits ? {
        edited_spec_name_translated: editedName,
        edited_spec_text_translated: editedText
      } : {};
      
      await approveRequirement(requirementId, data);
      showSuccess('Requirement approved successfully');
      setEditingReq(null);
      loadRequirements();
      if (onRefreshCount) onRefreshCount();
    } catch (error) {
      showError('Error approving requirement');
      console.error('Error approving requirement:', error);
    }
  };
  
  const handleReject = async () => {
    if (!rejectReason.trim()) {
      showError('You must provide a reason for rejection');
      return;
    }
    
    try {
      await rejectRequirement(rejectingReq.id, rejectReason);
      showSuccess('Requirement rejected');
      setRejectingReq(null);
      setRejectReason('');
      loadRequirements();
      if (onRefreshCount) onRefreshCount();
    } catch (error) {
      showError('Error rejecting requirement');
      console.error('Error rejecting requirement:', error);
    }
  };

  const handleDeleteAll = async () => {
    try {
      const data = await deleteAllPendingRequirements();
      showSuccess(data.message || 'All pending requirements deleted');
      setConfirmDeleteAll(false);
      loadRequirements();
      if (onRefreshCount) onRefreshCount();
    } catch (error) {
      showError('Error deleting all pending requirements');
      console.error('Error deleting all pending requirements:', error);
    }
  };
  
  const openEditDialog = (req) => {
    setEditingReq(req);
    setEditedName(req.spec_name_translated || '');
    setEditedText(req.spec_text_translated || '');
  };
  
  if (!canValidate) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">
          You don't have permissions to validate requirements. This feature is only available for Admin and Editor users.
        </Alert>
      </Container>
    );
  }
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
              New Requirements Validation
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Review and approve master requirements that were automatically created
              without a match in the database.
            </Typography>
            <Chip 
              label={`${pagination.total_items} pending`}
              color="primary"
              sx={{ mt: 2 }}
            />
          </Box>
          {userPermissions?.is_admin && requirements.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Cancel />}
              onClick={() => setConfirmDeleteAll(true)}
            >
              Delete all pending
            </Button>
          )}
        </Box>
      </Box>
      
      {/* Lista de requisitos pendientes */}
      {requirements.length === 0 ? (
        <Alert severity="success">
          No requirements pending validation. Everything is up to date!
        </Alert>
      ) : (
        <>
          <Grid container spacing={3}>
            {requirements.map((req) => (
              <Grid item xs={12} key={req.id}>
                <Card elevation={3}>
                  <CardHeader
                    avatar={<FilePresent color="primary" />}
                    title={
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {req.spec_code}
                      </Typography>
                    }
                    subheader={
                      <Box>
                        <Chip 
                          label="New requirement - Requires validation"
                          color="warning"
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                        {req.source_info && (
                          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                            Originated from: {req.source_info.ttl_file_name}
                          </Typography>
                        )}
                      </Box>
                    }
                    action={
                      <Tooltip title="Requirement information">
                        <IconButton>
                          <Info />
                        </IconButton>
                      </Tooltip>
                    }
                  />
                  <Divider />
                  <CardContent>
                    {/* Texto original */}
                    <Accordion defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          📝 Original Text
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            <strong>Name:</strong>
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 2 }}>
                            {req.spec_name || 'No name'}
                          </Typography>
                          
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            <strong>Full text:</strong>
                          </Typography>
                          <Typography variant="body1">
                            {req.spec_text || 'No text'}
                          </Typography>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                    
                    {/* Texto traducido */}
                    <Accordion defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          🌐 Automatic Translation
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            <strong>Translated name:</strong>
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 2 }}>
                            {req.spec_name_translated || 'No translation'}
                          </Typography>
                          
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            <strong>Translated text:</strong>
                          </Typography>
                          <Typography variant="body1">
                            {req.spec_text_translated || 'No translation'}
                          </Typography>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                    
                    {/* Información del archivo fuente */}
                    {req.source_info && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            📂 Source File Information
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box>
                            <Typography variant="body2">
                              <strong>TTL File:</strong> {req.source_info.ttl_file_name}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Notation:</strong> {req.source_info.notation || 'N/A'}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              <strong>Original TTL text:</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              {req.source_info.requirement_text || 'N/A'}
                            </Typography>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </CardContent>
                  <Divider />
                  <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Edit />}
                      onClick={() => openEditDialog(req)}
                      sx={{ mr: 1 }}
                    >
                      Edit and Approve
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircle />}
                      onClick={() => handleApprove(req.id, false)}
                      sx={{ mr: 1 }}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<Cancel />}
                      onClick={() => setRejectingReq(req)}
                    >
                      Reject
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          {/* Paginación */}
          {pagination.total_pages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={pagination.total_pages}
                page={pagination.current_page}
                onChange={(e, page) => setPagination({ ...pagination, current_page: page })}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}
      
      {/* Dialog: Editar antes de aprobar */}
      <Dialog open={!!editingReq} onClose={() => setEditingReq(null)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Requirement before Approving</DialogTitle>
        <DialogContent>
          <TextField
            label="Translated name"
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={2}
          />
          <TextField
            label="Translated text"
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={6}
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            The changes you make will be saved in the master requirement.
            The translation will be automatically regenerated.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingReq(null)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="success"
            onClick={() => handleApprove(editingReq.id, true)}
            startIcon={<CheckCircle />}
          >
            Save and Approve
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog: Confirmar rechazo */}
      <Dialog open={!!rejectingReq} onClose={() => setRejectingReq(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Requirement</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This requirement will be permanently deleted. This action cannot be undone.
          </Alert>
          <TextField
            label="Rejection reason (required)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
            required
            placeholder="Explain why this requirement should be rejected..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setRejectingReq(null);
            setRejectReason('');
          }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleReject}
            startIcon={<Cancel />}
            disabled={!rejectReason.trim()}
          >
            Confirm Rejection
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Confirmar borrado masivo (solo Admin) */}
      <Dialog open={confirmDeleteAll} onClose={() => setConfirmDeleteAll(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete all pending requirements</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This will permanently delete <strong>all {pagination.total_items} pending requirements</strong>.
            This action cannot be undone.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            The corresponding TTL requirements will be unlinked and set back to "pending" match status.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteAll(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<Cancel />}
            onClick={handleDeleteAll}
          >
            Delete all
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PendingRequirementsPanel;
